import React, { useState } from 'react';
import Editor from '@monaco-editor/react';
import "../CodeConverter.css";
import { FaDownload, FaCompress, FaBrush, FaCopy, FaCheck, FaTrash } from "react-icons/fa";
import JsonEditorPanel from "../components/JsonEditorPanel";
import CommonSeoContent from "../components/CommonSeoContent";
import useEditorActions from "../utils/useEditorActions";

const JsonToJava = () => {
  const [inputCode, setInputCode] = useState("");
  const [outputCode, setOutputCode] = useState("");

  // Import reusable actions
  const { handleDownload, handleClear } = useEditorActions(
    setInputCode,
    outputCode,
    setOutputCode
  );

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const inferType = (value, key) => {
    if (typeof value === 'string') return 'String';
    if (typeof value === 'number') return Number.isInteger(value) ? 'int' : 'double';
    if (typeof value === 'boolean') return 'boolean';
    if (Array.isArray(value)) {
      if (value.length > 0 && typeof value[0] === 'object') {
        return `List<${capitalize(key)}Item>`;
      }
      return 'List<String>';
    }
    if (typeof value === 'object' && value !== null) return capitalize(key);
    return 'String';
  };

  const parseObject = (obj, className = 'Root') => {
    let classCode = `public class ${className} {\n`;
    let innerClasses = '';

    for (let key in obj) {
      const value = obj[key];
      const fieldType = inferType(value, key);

      // Add field to current class
      classCode += `  private ${fieldType} ${key};\n`;

      // If nested object
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        innerClasses += '\n' + parseObject(value, capitalize(key));
      }

      // If array of objects
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
        innerClasses += '\n' + parseObject(value[0], `${capitalize(key)}Item`);
      }
    }

    classCode += '}\n';
    return classCode + innerClasses;
  };

  const handleConvert = () => {
    try {
      const json = JSON.parse(inputCode);
      const className = 'Root';
      const result = parseObject(json, className);
      setOutputCode(result);
    } catch (e) {
      setOutputCode("// ❌ Invalid JSON:\n" + e.message);
    }
  };

  return (
    <div className="formatter-container">
      <h1>JSON ➝ JAVA Converter </h1>

      {/* Toolbar */}
      <div className="toolbar">
        <button onClick={handleConvert} className="btn primary">
          <FaBrush /> Convert
        </button>
        <button onClick={() => handleDownload("java", "formatted")} className="btn success">
          <FaDownload /> Download
        </button>
		<button onClick={handleClear} className="btn danger">
          <FaTrash /> Clear
        </button>

      </div>

      {/* Editors */}
      <div className="editor-panels">
        <JsonEditorPanel title="Input JSON" language="json" code={inputCode} setCode={setInputCode} />
        <div className="editor-section-empty-div">
        </div>
        <JsonEditorPanel title="Output Java" language="java"  code={outputCode} />
      </div>
	  
	  <CommonSeoContent
	  title="Online JSON to Java Class Converter"
	  description="Convert JSON data into Java POJO classes instantly with jsonconverter.org. Supports nested JSON and generates clean, type-safe Java code for developers."
	  keywords="JSON to Java, json to java code generator, json to java converter, json to pojo, java class generator, online java converter, jsonconverter.org"
	  author="jsonconverter.org"
	  heading="Why Use JSON to Java Converter?"
	  whyUseText="Our JSON to Java Converter helps you automatically generate Java POJO classes from JSON data. Whether you're building RESTful APIs, mobile apps, or integrating third-party services, this tool simplifies your workflow by creating ready-to-use Java classes that reflect the structure of your JSON. It supports nested JSON objects and arrays, accurately mapping them to appropriate Java types. Since the tool runs entirely in your browser, your data remains secure and private. It’s fast, developer-friendly, and free to use."
	  howToUseSteps={[
		"Paste your JSON into the input editor.",
		"Click the Convert button.",
		"View your generated Java class output instantly.",
		"Copy or download the Java code for use in your project.",
	  ]}
	  features={[
		"Generates clean, readable Java POJOs from JSON input",
		"Supports nested JSON objects and arrays with proper Java type mapping",
		"Handles common types like int, double, boolean, String, and Lists",
		"Runs completely in-browser — no data is sent to a server",
		"No installation or registration required — fast and free",
		"Option to download the generated Java code for offline use",
	  ]}
	  inputTitle="Input JSON:"
	  inputExample={`
{
"name": "John Doe",
"age": 30,
"isActive": true,
"emails": ["john@example.com"],
"profile": {
"bio": "Developer",
"website": "https://jsonconverter.org"
}
}`}
	  outputTitle="Output Java:"
	  outputExample={`
public class User {
	  private String name;
	  private int age;
	  private boolean isActive;
	  private List<String> emails;
	  private Profile profile;

	  // Getters and Setters
	}

public class Profile {
	  private String bio;
	  private String website;

	  // Getters and Setters
	}`}
	  callToAction="Start converting your JSON to Java classes instantly — no signup required."
	/>
    </div>
  );
};

export default JsonToJava;
